import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { ToastMessageOptions } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  messageSubject = new Subject<ToastMessageOptions>();

  constructor(
    private translate: TranslateService
  ) { }

  error(message: string, life = 5000): void {
    this.messageSubject.next({
      severity: 'error',
      detail: message,
      life: life,
      closable: false,
    });
  }

  warning(message: string, life = 5000): void {
    this.messageSubject.next({
      severity: 'warn',
      detail: message,
      life: life,
      closable: false,
    });
  }

  success(message: string, life = 3000): void {
    this.messageSubject.next({
      severity: 'success',
      detail: message,
      life: life,
      closable: false,
    });
  }

  showBackendError(error: any, fallback?: string): void {
    const status = error?.status;
    const fallbackMsg = fallback || this.translate.instant('ERRORS.GENERIC');

    if (status >= 400 && status < 500) {
      const code = NotificationService.extractErrorCode(error);
      if (!code) {
        this.error(fallbackMsg);
        return;
      }
      const translationKey = `ERRORS.${code}`;
      this.translate.get(translationKey).subscribe(translatedMsg => {
        const finalMsg = translatedMsg !== translationKey
          ? translatedMsg
          : NotificationService.humanizeBackendMessage(code);
        this.error(finalMsg);
      });
    } else if (status >= 500) {
      this.error(this.translate.instant('ERRORS.SERVER_ERROR') || fallbackMsg);
    } else {
      this.error(fallbackMsg);
    }
  }

  private static extractErrorCode(error: any): string | null {
    const body = error?.error;
    if (body != null) {
      if (typeof body === 'object' && typeof body.message === 'string' && body.message) {
        return body.message;
      }
      if (typeof body === 'string' && body.trimStart().startsWith('{')) {
        try {
          const parsed = JSON.parse(body);
          if (typeof parsed?.message === 'string' && parsed.message) {
            return parsed.message;
          }
        } catch { }
      }
    }
    return null;
  }

  private static humanizeBackendMessage(raw: string): string {
    const message = raw.trim();
    const isCode = /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$/.test(message);
    if (!isCode) {
      return message;
    }
    return message
      .toLowerCase()
      .split('_')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

}
